/* eslint-disable sonarjs/cognitive-complexity */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import RichText from '@tcp/core/src/components/common/atoms/RichText';
import SpinnerOverlay from '@tcp/core/src/components/common/atoms/SpinnerOverlay';
import BodyCopy from '../../../../../../common/atoms/BodyCopy';
import Button from '../../../../../../common/atoms/Button';
import withStyles from '../../../../../../common/hoc/withStyles';
import styles from '../styles/LoginOTPFormV2.styles';
import OTPInputView from '../../../../OTPInput/views/OTPInput.view';
import Recaptcha from '../../../../../../common/molecules/recaptcha/recaptcha';
import TextBox from '../../../../../../common/atoms/TextBox';
import createValidateMethod from '../../../../../../../utils/formValidation/createValidateMethod';
import getStandardConfig from '../../../../../../../utils/formValidation/validatorStandardConfig';
import Anchor from '../../../../../../common/atoms/Anchor';
import LineSeperator from '../../../atoms/LineSeperator';
import LoginOptionView from '../../LoginOptions/views';
import {
  getDynamicLabelValues,
  getLabelFromCopyOrFromTranslation,
} from '../../../../../../../utils/labels/labelUtilty';
import {
  GLOBAL_LOGIN_LABELS,
  GlobalLoginLabels,
} from '../../../../../../../utils/labels/globalLogin.labels';
import {
  GLOBAL_PASSWORD_LABELS,
  GlobalPasswordsLabels,
} from '../../../../../../../utils/labels/globalPassword.labels';
import {
  GLOBAL_HELPCENTER_LABELS,
  GlobalHelpCenterLabels,
} from '../../../../../../../utils/labels/globalHelpCenter.labels';
import {
  GLOBAL_ACTIONS_LABELS,
  GlobalActionsLabels,
  lblLogin,
} from '../../../../../../../utils/labels/globalActions.labels';

const OR = 'or';
const emailSmsHandler = (e, handler, type) => {
  e.preventDefault();
  handler({ otpChannel: type });
};
const getLoginOptions = (
  passwordSubmitHandler,
  loginSelectedOption,
  isLoginWithEmailOTPEnabled,
  emailAddress,
  generateNewCode,
  maskedPhoneNumber,
  isShowPhoneLoginOption,
  isCanada,
  fromPlccICLogin,
  showSMSOption
) => {
  return (
    <>
      <LoginOptionView
        type="password"
        submitForm={passwordSubmitHandler}
        fromPlccICLogin={fromPlccICLogin}
      />
      {loginSelectedOption !== 'email' && isLoginWithEmailOTPEnabled && (
        <LoginOptionView
          type="email"
          emailAddress={emailAddress}
          submitForm={(e) => emailSmsHandler(e, generateNewCode, 'email')}
          fromPlccICLogin={fromPlccICLogin}
        />
      )}
      {showSMSOption && loginSelectedOption !== 'sms' && isShowPhoneLoginOption && !isCanada && (
        <LoginOptionView
          type="sms"
          maskedPhoneNumber={maskedPhoneNumber}
          submitForm={(e) => emailSmsHandler(e, generateNewCode, 'sms')}
          fromPlccICLogin={fromPlccICLogin}
        />
      )}
    </>
  );
};
const showSmsOption = (phoneCountryCode) => {
  return phoneCountryCode ? phoneCountryCode === '+1' : true;
};

const LoginOTPFormV2 = ({
  errorMessage,
  isLoading,
  showRecaptcha,
  handleSubmit,
  change,
  generateOTP,
  newCodeSent,
  loginErrorMessage,
  showPasswordScreen,
  resetLoginState,
  isV2Flow,
  pristine,
  className,
  trackAnalyticsClick,
  invalid,
  loginSelectedOption,
  emailAddress,
  isLoginWithEmailOTPEnabled,
  maskedPhoneNumber,
  isShowPhoneLoginOption,
  isLoginModal,
  isCanada,
  fromPlccICLogin,
  isPlccLoginLoading,
  phoneCountryCode,
}) => {
  const [resetPinField, setResetPinField] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [loginClicked, setLoginClicked] = useState(false);
  const [otp, setOtp] = useState('');
  const handleDisabled = () => {
    setIsDisabled(otp.length < 6);
  };

  const { lbl_signin_with_pwd: lblSigninWithPwd, lbl_login_loginAndCheckout: lblLoginAndCheckout } =
    getLabelFromCopyOrFromTranslation(GLOBAL_LOGIN_LABELS, GlobalLoginLabels);
  const {
    lbl_verify_otp: lblVerifyOtp,
    lbl_resend_it: lblResendIt,
    lbl_phone_otp_not_received_content: lblPhoneOtpNotReceived,
  } = getLabelFromCopyOrFromTranslation(GLOBAL_PASSWORD_LABELS, GlobalPasswordsLabels);
  const { lbl_merge_new_code_sent: lblMergeNewCodeSent } = getLabelFromCopyOrFromTranslation(
    GLOBAL_HELPCENTER_LABELS,
    GlobalHelpCenterLabels
  );

  const resetError = () => {
    if (loginErrorMessage) {
      resetLoginState();
    }
  };

  useEffect(() => {
    if (isLoading === false) {
      setLoginClicked(false);
      change('recaptchaToken', '');
    }
  }, [isLoading]);

  useEffect(() => {
    if (resetPinField) {
      setOtp('');
      setResetPinField(false);
    }
    handleDisabled();
    change('otp', otp);
  }, [otp, resetPinField]);

  const generateNewCode = (payload) => {
    resetError();
    generateOTP(payload);
    setOtp('');
  };

  const shouldLoginButtonDisabled = () => isDisabled || isLoading || pristine || invalid;

  const getActiveClassName = () => {
    if (loginErrorMessage) {
      return 'invalid-otp';
    }
    if (errorMessage && errorMessage.size > 0) {
      return 'invalid-otp';
    }
    if (otp && otp.length === 0) {
      return '';
    }
    if (otp && otp.length === 6) {
      return 'valid-otp';
    }
    return '';
  };

  const showPasswordInputPage = (e) => {
    e.preventDefault();
    resetError();
    trackAnalyticsClick({
      metric33: '1',
      eVar150: lblSigninWithPwd,
      customEvents: ['event33'],
    });
    showPasswordScreen();
  };
  const showSMSOption = showSmsOption(phoneCountryCode);
  const recaptchaContent = () => {
    return (
      <BodyCopy component="div">
        <Recaptcha
          verifyCallback={(token) => change('recaptchaToken', token)}
          expiredCallback={() => change('recaptchaToken', '')}
        />
        <Field
          component={TextBox}
          type="hidden"
          name="recaptchaToken"
          data-locator="otp-recaptchcb"
        />
      </BodyCopy>
    );
  };

  return (
    <>
      {isLoading && !loginClicked && (
        <SpinnerOverlay
          isLoginModal={isLoginModal}
          isNewLoginScreen={true}
          fromPlccICLogin={fromPlccICLogin}
        />
      )}
      <div className={className}>
        <form name="OTPForm" onSubmit={handleSubmit} noValidate>
          <BodyCopy
            component="div"
            textAlign={fromPlccICLogin ? 'left' : 'center'}
            className="elem-mb-LRG"
          >
            <BodyCopy
              component="p"
              fontSize="fs16"
              fontWeight="extrabold"
              fontFamily="secondary"
              textAlign={fromPlccICLogin ? 'left' : 'center'}
              className="elem-mb-SM"
            >
              {lblVerifyOtp}
            </BodyCopy>
            <BodyCopy component="div" textAlign="center" className="otp-wrapper">
              <OTPInputView
                value={otp}
                onChange={setOtp}
                numInputs={6}
                separator={<span className="seperator">-</span>}
                inputStyle={getActiveClassName()}
                containerStyle="otp-container"
                className="otp-input-container"
                isInputNum
                shouldAutoFocus
                isResetViaText
              />
              {!loginErrorMessage && newCodeSent && (
                <BodyCopy
                  fontSize="fs12"
                  fontWeight="semibold"
                  color="green.500"
                  className="elem-mt-XS test-newCode"
                >
                  {lblMergeNewCodeSent}
                </BodyCopy>
              )}
              {loginErrorMessage && (
                <BodyCopy
                  fontSize="fs12"
                  fontWeight="semibold"
                  color="red.500"
                  className="login-error"
                >
                  <RichText className="richTextColor" richTextHtml={loginErrorMessage} />
                </BodyCopy>
              )}
            </BodyCopy>
          </BodyCopy>
          <BodyCopy component="div" className="recaptcha_loginotp elem-mt-MED">
            {showRecaptcha && recaptchaContent()}
          </BodyCopy>
          {!fromPlccICLogin && (
            <BodyCopy component="div" textAlign="center">
              <Button
                className="elem-mb-MED elem-mt-MED"
                id="subBtn"
                fill="BLUE"
                type="submit"
                buttonVariation="fixed-width"
                dataLocator="login-logincta"
                fullWidth
                showBorderRadiusStyle
                disabled={shouldLoginButtonDisabled()}
                loading={isLoading && loginClicked}
                spc
                onClick={() => setLoginClicked(true)}
              >
                {isV2Flow && lblLoginAndCheckout}
                {!isV2Flow &&
                  getLabelFromCopyOrFromTranslation(
                    `${GLOBAL_ACTIONS_LABELS}.${lblLogin}`,
                    GlobalActionsLabels
                  )}
              </Button>
            </BodyCopy>
          )}
          <BodyCopy
            component="div"
            textAlign={fromPlccICLogin ? 'left' : 'center'}
            className="elem-mb-XS"
          >
            <p className="resend-code-text">
              {lblPhoneOtpNotReceived}
              &nbsp;
              <Anchor
                underline
                fontSizeVariation="large"
                anchorVariation="primary"
                dataLocator="login-forgotpasswordlnk"
                onClick={(e) => {
                  e.preventDefault();
                  generateNewCode({ otpChannel: loginSelectedOption });
                }}
                className="resend-it"
              >
                {lblResendIt}
              </Anchor>
            </p>
            <BodyCopy
              className="elem-mb-MED elem-mt-LRG"
              component="p"
              textAlign="center"
              fontSize="fs14"
              fontWeight="semibold"
              fontFamily="secondary"
            >
              {OR}
            </BodyCopy>
            <LineSeperator />
            {getLoginOptions(
              showPasswordInputPage,
              loginSelectedOption,
              isLoginWithEmailOTPEnabled,
              emailAddress,
              generateNewCode,
              maskedPhoneNumber,
              isShowPhoneLoginOption,
              isCanada,
              fromPlccICLogin,
              showSMSOption
            )}
          </BodyCopy>
          {fromPlccICLogin && (
            <div className="plcc-footer-cta-section">
              <BodyCopy className="plcc-login-cta-container" component="div" textAlign="left">
                <Button
                  className="elem-mb-LRG elem-mt-LRG plcc-border-style"
                  id="subBtn"
                  fill="BLUE"
                  type="submit"
                  buttonVariation="fixed-width"
                  dataLocator="login-logincta"
                  fullWidth
                  showBorderRadiusStyle
                  disabled={shouldLoginButtonDisabled()}
                  loading={(isLoading && loginClicked) || isPlccLoginLoading}
                  spc
                  onClick={() => setLoginClicked(true)}
                >
                  {getDynamicLabelValues('dynamic.cart.lbl_plcc_ic_login_modal_cta_text')}
                </Button>
              </BodyCopy>
            </div>
          )}
        </form>
      </div>
    </>
  );
};

LoginOTPFormV2.propTypes = {
  handleSubmit: PropTypes.func,
  setpasswordValidationCode: PropTypes.func,
  labels: PropTypes.shape({}).isRequired,
  errorMessage: PropTypes.string,
  isLoading: PropTypes.bool,
  showRecaptcha: PropTypes.bool,
  isRecaptchaError: PropTypes.bool,
  isShowRecaptchaError: PropTypes.bool,
  change: PropTypes.func,
  generateOTP: PropTypes.func,
  loginErrorMessage: PropTypes.string,
  newCodeSent: PropTypes.bool,
};

LoginOTPFormV2.defaultProps = {
  errorMessage: '',
  setpasswordValidationCode: () => {},
  showRecaptcha: true,
  isRecaptchaError: false,
  isShowRecaptchaError: false,
  change: () => {},
  generateOTP: () => {},
  loginErrorMessage: '',
  newCodeSent: false,
};

const validateMethod = createValidateMethod(getStandardConfig(['recaptchaToken']));

export default reduxForm({
  form: 'OTPForm', // a unique identifier for this form
  enableReinitialize: true,
  ...validateMethod,
})(withStyles(LoginOTPFormV2, styles));
export { LoginOTPFormV2 as LoginOTPFormVanilla };
